<template>
  <div class="tl-view">
    <!-- The path at a glance: each stop once -->
    <div class="tl-path" aria-label="Path so far">
      <template v-for="(stop, i) in stops">
        <v-icon v-if="i > 0" :key="'arrow' + i" small color="#94a3b8">mdi-chevron-right</v-icon>
        <span :key="'chip' + i" class="tl-path-chip" :class="{ 'tl-path-chip--current': i === stops.length - 1 }">
          {{ stopRole(stop) }}
        </span>
      </template>
    </div>

    <!-- One entry per stop: who it reached and what they did with it -->
    <ol class="tl-list">
      <li v-for="(stop, i) in stops" :key="i" class="tl-step" :class="{ 'tl-step--latest': i === stops.length - 1 }">
        <div class="tl-marker">
          <span class="tl-dot" :class="'tl-dot--' + stopTone(stop)">
            <v-icon small>{{ roleIcon(stop) }}</v-icon>
          </span>
        </div>

        <div class="tl-content">
          <div class="tl-step-head">
            <div class="tl-who">
              <span class="tl-role" :class="'tl-role--' + stopTone(stop)">{{ stopRole(stop) }}</span>
              <span v-if="stopName(stop)" class="tl-person-name">{{ stopName(stop) }}</span>
              <span v-else-if="!isClosed" class="tl-desk-note">Any {{ stopRole(stop) }} can act on it</span>
            </div>
            <span v-if="i === stops.length - 1" class="tl-latest">{{ isClosed ? "Latest" : "Currently here" }}</span>
          </div>

          <!-- What was done at this stop -->
          <div v-for="(act, a) in stop.actions" :key="a" class="tl-action">
            <div class="tl-arrival">
              <v-icon x-small color="#15803d">{{ act.to ? "mdi-send-outline" : "mdi-flag-checkered" }}</v-icon>
              <span>
                {{ act.label }}
                <template v-if="act.to">
                  to <b>{{ isApplicant(act.to) ? "Applicant" : personRole(act.to) || personName(act.to) }}</b>
                </template>
              </span>
              <span v-if="act.at" class="tl-time">&middot; {{ formatTimestamp(act.at) }}</span>
            </div>
            <div v-if="act.remarks" class="tl-remarks">
              <v-icon x-small color="#b45309">mdi-message-text-outline</v-icon>
              <div>
                <span class="tl-remarks-label">Remarks</span>
                {{ act.remarks }}
              </div>
            </div>
          </div>

          <!-- Hasn't acted yet -->
          <div v-if="!stop.actions.length" class="tl-arrival tl-arrival--waiting">
            <v-icon x-small color="#94a3b8">mdi-inbox-arrow-down-outline</v-icon>
            <span>Received</span>
            <span v-if="stop.receivedAt" class="tl-time">&middot; {{ formatTimestamp(stop.receivedAt) }}</span>
            <span v-if="!isClosed" class="tl-waiting">Awaiting action</span>
          </div>
        </div>
      </li>
    </ol>
  </div>
</template>

<script>
import moment from "moment";
import { ROLE_NAMES } from "@/components/Common/deskConfig";

const ROLE_ICONS = {
  Applicant: "mdi-account-outline",
  "Section Clerk": "mdi-file-document-edit-outline",
  Superintendent: "mdi-account-tie-outline",
  DFO: "mdi-pine-tree",
  "Range Officer": "mdi-map-marker-radius-outline",
  Admin: "mdi-shield-account-outline",
  "Super Admin": "mdi-shield-crown-outline",
};

// Records of an application that are steps in its journey (fee/amount edits
// are logged too, but they aren't moves).
export function journeyRecords(records) {
  return (records || []).filter((item) => {
    if (!item || !(item.forwardFrom || item.forwardTo)) return false;
    const p = (item.processDone || "").toLowerCase();
    return !(
      p.includes("amount updated") ||
      p.includes("fee updated") ||
      p.includes("security should be remitted") ||
      p.includes("deposit amount updated")
    );
  });
}

// Timeline of a film shooting application, laid out like Nature Camp's: the
// path of stops at a glance, then one entry per stop with what was done
// there, by whom, and the remarks. Used by TimelineDialog and Track
// Applications.
export default {
  name: "TimelineView",
  props: {
    records: { type: Array, default: () => [] },
    application: { type: Object, default: () => ({}) },
  },
  computed: {
    // Each desk/person the application has reached, once and in order. Every
    // record is shown on the stop of whoever did it (the sender), so remarks
    // sit with whoever wrote them.
    stops() {
      const recs = this.records || [];
      if (!recs.length) return [];
      const stops = [{ person: recs[0].forwardFrom, actions: [], receivedAt: null }];
      recs.forEach((r) => {
        const at = r.forwardDate || r.created_at;
        stops[stops.length - 1].actions.push({
          by: r.forwardFrom,
          label: r.processDone || "Forwarded",
          to: r.forwardTo || null,
          at,
          remarks: r.remarks,
        });
        if (r.forwardTo) stops.push({ person: r.forwardTo, actions: [], receivedAt: at });
      });
      return stops;
    },
    isClosed() {
      return !!this.application && !!this.application.status && this.application.status !== "Active";
    },
  },
  methods: {
    formatTimestamp(dateString) {
      if (!dateString) return "";
      return moment(dateString).format("DD-MM-YYYY, hh:mm A");
    },
    personName(userObj) {
      if (!userObj) return "";
      if (typeof userObj === "string") return userObj;
      return userObj.username || userObj.name || ROLE_NAMES[Number(userObj.uType)] || "Official";
    },
    personRole(userObj) {
      if (!userObj || typeof userObj === "string") return "";
      return ROLE_NAMES[Number(userObj.uType)] || "";
    },
    isApplicant(userObj) {
      return !!userObj && typeof userObj === "object" && Number(userObj.uType) === 772;
    },
    roleIcon(stop) {
      return ROLE_ICONS[this.personRole(stop.person)] || "mdi-account-circle-outline";
    },
    stopRole(stop) {
      if (this.isApplicant(stop.person)) return "Applicant";
      return this.personRole(stop.person) || "Officer";
    },
    // Name shown on a stop: the applicant, or the official who acted there.
    // While it waits at a desk any officer in that role can take it up, so
    // no one is named until someone does.
    stopName(stop) {
      if (this.isApplicant(stop.person)) return this.personName(stop.person);
      const act = stop.actions[0];
      return act && act.by ? this.personName(act.by) : "";
    },
    stopTone(stop) {
      const role = this.personRole(stop.person);
      if (role === "Applicant") return "applicant";
      if (role === "DFO" || role === "Admin" || role === "Super Admin") return "decider";
      return "staff";
    },
  },
};
</script>

<style scoped>
.tl-path {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 2px;
  margin-bottom: 18px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e3eee6;
}

.tl-path-chip {
  padding: 3px 10px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #334155;
  font-size: 12.5px;
  font-weight: 700;
  white-space: nowrap;
}

.tl-path-chip--current {
  background: #15803d;
  color: #ffffff;
}

.tl-list {
  position: relative;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tl-step {
  position: relative;
  display: flex;
  gap: 14px;
  padding-bottom: 14px;
}

.tl-step:last-child {
  padding-bottom: 0;
}

/* rail between stops */
.tl-step::before {
  content: "";
  position: absolute;
  left: 17px;
  top: 38px;
  bottom: 0;
  width: 2px;
  background: #d7ebdd;
}

.tl-step:last-child::before {
  display: none;
}

.tl-marker {
  flex-shrink: 0;
  width: 36px;
  display: flex;
  justify-content: center;
}

.tl-dot {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 1px #d7ebdd;
}

.tl-dot .v-icon {
  color: inherit !important;
}

.tl-dot--applicant {
  background: #dbeafe;
  color: #1d4ed8;
}

.tl-dot--staff {
  background: rgba(21, 128, 61, 0.12);
  color: #15803d;
}

.tl-dot--decider {
  background: #fef3c7;
  color: #b45309;
}

.tl-step--latest .tl-dot {
  box-shadow: 0 0 0 3px rgba(21, 128, 61, 0.35);
}

.tl-content {
  flex: 1;
  min-width: 0;
  padding: 12px 14px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #e3eee6;
}

.tl-step--latest .tl-content {
  border-color: rgba(21, 128, 61, 0.4);
  box-shadow: 0 8px 22px rgba(21, 128, 61, 0.1);
}

.tl-step-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 10px;
}

.tl-who {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  min-width: 0;
}

.tl-role {
  padding: 1px 8px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 800;
  background: rgba(21, 128, 61, 0.1);
  color: #14532d;
}

.tl-role--applicant {
  background: #dbeafe;
  color: #1d4ed8;
}

.tl-role--decider {
  background: #fef3c7;
  color: #92400e;
}

.tl-person-name {
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
  word-break: break-word;
}

.tl-latest {
  padding: 1px 8px;
  border-radius: 999px;
  background: rgba(21, 128, 61, 0.1);
  color: #15803d;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.tl-arrival {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
  margin-top: 6px;
  font-size: 13px;
  color: #475569;
}

.tl-arrival b {
  color: #0f172a;
}

.tl-time {
  color: #64748b;
  white-space: nowrap;
}

.tl-action + .tl-action {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e3eee6;
}

.tl-arrival--waiting {
  color: #64748b;
}

.tl-waiting {
  padding: 1px 8px;
  border-radius: 999px;
  background: #fef3c7;
  color: #92400e;
  font-size: 11.5px;
  font-weight: 800;
}

.tl-desk-note {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}

.tl-remarks {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  font-size: 13px;
  color: #451a03;
  line-height: 1.45;
}

.tl-remarks .v-icon {
  margin-top: 3px;
}

.tl-remarks-label {
  font-weight: 800;
  color: #b45309;
  margin-right: 4px;
}

@media (max-width: 600px) {
  .tl-step {
    gap: 10px;
  }

  .tl-marker,
  .tl-dot {
    width: 30px;
  }

  .tl-dot {
    height: 30px;
  }

  .tl-step::before {
    left: 14px;
    top: 32px;
  }

  .tl-content {
    padding: 10px 12px;
  }

  .tl-path {
    flex-wrap: nowrap;
    overflow-x: auto;
  }
}
</style>
